/**
 * Account Type Utilities
 *
 * Outfame uses plan tiers: trial, go, grow, glory
 * Legacy names from Upgrow codebase: basic, pro, turbo (mapped to go, grow, glory)
 *
 * Database may contain either old or new names, so we normalize defensively.
 */

import { FIRST_MONTH_PRICES_CENTS, YEARLY_PRICES_CENTS, LEGACY_GLORY_YEARLY_PRICE_CENTS, PARITY_PRICING_CUTOFF_MS, type PriceTier } from "@/constants/pricing"

/**
 * Legacy account type mapping
 * Database may contain old Upgrow names that need normalization
 */
const LEGACY_TO_NEW: Record<string, string> = {
    basic: "go",
    pro: "grow",
    turbo: "glory"
}

/**
 * Plan tier hierarchy (lowest to highest)
 * Used for upgrade eligibility checks
 */
export const PLAN_HIERARCHY = ["trial", "go", "grow", "glory"] as const
export type PlanTier = (typeof PLAN_HIERARCHY)[number]

/**
 * Normalizes account_type to new naming convention
 * Handles both old (basic/pro/turbo) and new (go/grow/glory) names
 *
 * @param accountType - Raw account_type from database
 * @returns Normalized plan tier name
 *
 * @example
 * normalizeAccountType("basic") // "go"
 * normalizeAccountType("turbo") // "glory"
 * normalizeAccountType("grow")  // "grow"
 * normalizeAccountType("trial") // "trial"
 */
export function normalizeAccountType(accountType: string | null | undefined): string {
    if (!accountType) return "trial"
    const lower = accountType.toLowerCase()
    return LEGACY_TO_NEW[lower] || lower
}

/**
 * Checks if an account is on the highest tier (Glory)
 * Handles both legacy "turbo" and new "glory" names
 *
 * @param accountType - Raw account_type from database
 * @returns true if account is on Glory plan
 */
export function isOnGloryPlan(accountType: string | null | undefined): boolean {
    const normalized = normalizeAccountType(accountType)
    return normalized === "glory"
}

/**
 * Checks if an account can be upgraded (not on Glory plan)
 *
 * @param accountType - Raw account_type from database
 * @returns true if account can be upgraded
 */
export function isUpgradeable(accountType: string | null | undefined): boolean {
    return !isOnGloryPlan(accountType)
}

/**
 * Gets the display name for a plan tier
 * Capitalizes and uses new naming convention
 *
 * @param accountType - Raw account_type from database
 * @returns Display name (e.g., "Go", "Grow", "Glory", "Trial")
 */
export function getPlanDisplayName(accountType: string | null | undefined): string {
    const normalized = normalizeAccountType(accountType)
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

// ============================================================================
// Trial Expiration Utilities
// ============================================================================

/**
 * Trial duration in days
 * Changed from 14 days to 3 days for faster conversion
 */
export const TRIAL_DURATION_DAYS = 3

/**
 * Minimal account shape needed for trial calculations
 */
type AccountWithCreatedAt = {
    created_at?: string | null
    account_type?: string | null
}

/**
 * Time remaining breakdown for trial accounts
 */
export type TrialTimeRemaining = {
    days: number
    hours: number
    minutes: number
    isExpired: boolean
}

/**
 * Calculates when a trial account expires
 *
 * @param account - Account object with created_at field
 * @returns Expiration Date, or null if created_at is missing
 *
 * @example
 * getTrialExpiresAt({ created_at: "2024-01-01T00:00:00Z" })
 * // Returns Date for 2024-01-04T00:00:00Z (3 days later)
 */
export function getTrialExpiresAt(account: AccountWithCreatedAt): Date | null {
    if (!account.created_at) return null

    const createdAt = new Date(account.created_at)
    if (isNaN(createdAt.getTime())) return null

    const expiresAt = new Date(createdAt.getTime())
    expiresAt.setDate(expiresAt.getDate() + TRIAL_DURATION_DAYS)
    return expiresAt
}

/**
 * Checks if a trial account has expired
 * Non-trial accounts always return false (they don't expire)
 *
 * @param account - Account object with created_at and account_type
 * @returns true if trial is expired, false otherwise
 *
 * @example
 * isTrialExpired({ account_type: "trial", created_at: "2024-01-01T00:00:00Z" })
 * // Returns true if current date > 2024-01-04
 *
 * isTrialExpired({ account_type: "go", created_at: "2024-01-01T00:00:00Z" })
 * // Returns false (paid plans don't expire)
 */
export function isTrialExpired(account: AccountWithCreatedAt): boolean {
    const normalized = normalizeAccountType(account.account_type)
    if (normalized !== "trial") return false

    const expiresAt = getTrialExpiresAt(account)
    if (!expiresAt) return false

    return new Date() > expiresAt
}

/**
 * Gets detailed time remaining for a trial account
 * Returns zeroes with isExpired: true for expired/non-trial accounts
 *
 * @param account - Account object with created_at and account_type
 * @returns Object with days, hours, minutes, and isExpired flag
 *
 * @example
 * getTrialTimeRemaining({ account_type: "trial", created_at: "..." })
 * // { days: 2, hours: 5, minutes: 30, isExpired: false }
 */
export function getTrialTimeRemaining(account: AccountWithCreatedAt): TrialTimeRemaining {
    const expired: TrialTimeRemaining = { days: 0, hours: 0, minutes: 0, isExpired: true }

    const normalized = normalizeAccountType(account.account_type)
    if (normalized !== "trial") return expired

    const expiresAt = getTrialExpiresAt(account)
    if (!expiresAt) return expired

    const now = new Date()
    const diffMs = expiresAt.getTime() - now.getTime()

    if (diffMs <= 0) return expired

    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    return {
        days: diffDays,
        hours: diffHours % 24,
        minutes: diffMinutes % 60,
        isExpired: false
    }
}

// ============================================================================
// CC Trial Utilities
// ============================================================================

/**
 * Minimal account shape needed for CC trial calculations
 */
type AccountWithCCTrial = {
    cc_trial_ends_at?: string | null
}

/**
 * Checks if an account is currently in a CC trial
 * Returns true if cc_trial_ends_at exists and is in the future
 *
 * @param account - Account object with cc_trial_ends_at field
 * @returns true if account is in an active CC trial
 *
 * @example
 * isCCTrial({ cc_trial_ends_at: "2026-03-01T00:00:00Z" })
 * // Returns true if current date < 2026-03-01
 *
 * isCCTrial({ cc_trial_ends_at: null })
 * // Returns false
 */
export function isCCTrial(account: AccountWithCCTrial): boolean {
    if (!account.cc_trial_ends_at) return false

    const endsAt = new Date(account.cc_trial_ends_at)
    if (isNaN(endsAt.getTime())) return false

    return new Date() < endsAt
}

/**
 * Time remaining breakdown for CC trial accounts
 */
export type CCTrialTimeRemaining = {
    days: number
    hours: number
    minutes: number
    totalMs: number
}

/**
 * Gets detailed time remaining for a CC trial account
 * Returns null if not in an active CC trial
 *
 * @param account - Account object with cc_trial_ends_at field
 * @returns Object with days, hours, minutes, totalMs — or null
 *
 * @example
 * getCCTrialTimeRemaining({ cc_trial_ends_at: "2026-03-01T00:00:00Z" })
 * // { days: 2, hours: 5, minutes: 30, totalMs: 192600000 }
 */
export function getCCTrialTimeRemaining(account: AccountWithCCTrial): CCTrialTimeRemaining | null {
    if (!account.cc_trial_ends_at) return null

    const endsAt = new Date(account.cc_trial_ends_at)
    if (isNaN(endsAt.getTime())) return null

    const totalMs = endsAt.getTime() - new Date().getTime()
    if (totalMs <= 0) return null

    const totalMinutes = Math.floor(totalMs / (1000 * 60))
    const totalHours = Math.floor(totalMinutes / 60)
    const days = Math.floor(totalHours / 24)

    return {
        days,
        hours: totalHours % 24,
        minutes: totalMinutes % 60,
        totalMs
    }
}

/**
 * Returns the amount in cents that will be charged when a CC trial converts.
 * Yearly plans charge the full yearly price, monthly plans charge the first-month price.
 */
export function getCCTrialChargeAmount(account: { billing_period?: string | null; account_type?: string | null; cc_trial_ends_at?: string | null }): number {
    const tier = normalizeAccountType(account.account_type) as PriceTier
    if (account.billing_period === "yearly") {
        // A Glory yearly trial started before the parity cutoff is on plan-glory-1188 and
        // converts at $1,188 (Day-3 charge and skip-trial both charge the invoice total).
        if (tier === "glory" && account.cc_trial_ends_at) {
            const trialStartMs = new Date(account.cc_trial_ends_at).getTime() - 3 * 24 * 60 * 60 * 1000
            if (Number.isFinite(trialStartMs) && trialStartMs < PARITY_PRICING_CUTOFF_MS) return LEGACY_GLORY_YEARLY_PRICE_CENTS
        }
        return YEARLY_PRICES_CENTS[tier] || YEARLY_PRICES_CENTS.go
    }
    return FIRST_MONTH_PRICES_CENTS[tier] || FIRST_MONTH_PRICES_CENTS.go
}
